import React, { useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { getTodayLocalDateString, getDateWithOffset } from '../utils/dateUtils';

function AddToMealPlanModal({ 
  show, 
  onHide, 
  recipe,
  servings,
  setServings,
  onSubmit,
  isLoading,
  error,
  zIndex = 10000
}) {
  const [selectedPlan, setSelectedPlan] = useState('');
  const [selectedPlannedCookedDate, setSelectedPlannedCookedDate] = useState('');
  const [dateError, setDateError] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setDateError('');
    
    if (plan === 'this-week') {
      setSelectedPlannedCookedDate(getTodayLocalDateString());
    } else if (plan === 'next-week') {
      setSelectedPlannedCookedDate(getDateWithOffset(7));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate that a date option is selected
    if (!selectedPlan) {
      setDateError('Please select when you want to cook this meal');
      return;
    }

    // Clear any previous errors
    setDateError('');

    // Call the parent's submit handler with the meal data
    const success = await onSubmit({
      selectedPlannedCookedDate,
      servings
    });

    // If successful, reset and close
    if (success) {
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedPlan('');
    setSelectedPlannedCookedDate('');
    setDateError('');
    setShowCalendar(false);
    onHide();
  };

  return (
    <Modal 
      show={show} 
      onHide={handleClose}
      style={{ zIndex }}
      onClick={e => e.stopPropagation()}
    >
      <Modal.Header closeButton>
        <Modal.Title>Add {recipe.name} to Meal Plan</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form className="add-to-meal-plan-modal">
          <Form.Group className="mb-3">
            <Form.Label>Add to</Form.Label>
            <div className="d-flex gap-2">
              <Button 
                variant={selectedPlan === 'this-week' ? 'primary' : 'outline-primary'}
                onClick={() => handlePlanSelect('this-week')}
                className={`custom-modal-button-option ${selectedPlan === 'this-week' ? 'selected' : ''}`}
              >
                This week
              </Button>
              <Button 
                variant={selectedPlan === 'next-week' ? 'primary' : 'outline-primary'}
                onClick={() => handlePlanSelect('next-week')}
                className={`custom-modal-button-option ${selectedPlan === 'next-week' ? 'selected' : ''}`}
              >
                Next week
              </Button>
              {/* Calendar option - currently commented out but preserved for future use */}
              {showCalendar && selectedPlan === 'other' && (
                <Form.Control
                  type="date"
                  style={{
                    backgroundColor: '#fff',
                    border: '1px solid #ced4da',
                    borderRadius: '8px',
                    width: '30%'
                  }}
                  onChange={(e) => setSelectedPlannedCookedDate(e.target.value)}
                  min={getTodayLocalDateString()}
                />
              )}
            </div>
            {dateError && (
              <div className="text-danger mt-2" style={{ fontSize: '0.875rem' }}>
                {dateError}
              </div>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Servings</Form.Label>
            <div className="d-flex align-items-center gap-2">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  if (servings > 1) {
                    setServings(servings - 1);
                  }
                }}
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}
              >
                -
              </Button>
              <Form.Control 
                type="number" 
                min="1"
                max="16"
                value={servings}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  if (value > 16) {
                    setServings(16);
                  } else if (value < 1) {
                    setServings(1);
                  } else {
                    setServings(value);
                  }
                }}
                style={{
                  textAlign: 'center',
                  width: '80px'
                }}
              />
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  if (servings < 16) {
                    setServings(servings + 1);
                  }
                }}
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}
              >
                +
              </Button>
            </div>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSubmit} 
          className="custom-modal-button-submit"
          disabled={isLoading}
          style={{
            backgroundColor: '#52cd92',
            borderColor: '#52cd92 !important',
            color: 'white'
          }}
        >
          {isLoading ? 'Adding...' : 'Add to Meal Plan'}
        </Button>
      </Modal.Footer>
      {error && (
        <div className="alert alert-danger m-3" role="alert">
          Failed to add to Meal Plan. Please try again later.
        </div>
      )}
    </Modal>
  );
}

export default AddToMealPlanModal;

